import React, { useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import Loading from 'flow/components/ui-components/Loading';
import ContentWrapper from 'flow/route-components/ContentWrapper';
import SupervisorOverviewComponent from 'flow/components/pbx/supervisor/Overview/SupervisorOverviewComponent';
import Spacer from 'flow/components/custom-components/spacer';
import {
    fetchOperators,
    fetchOperatorData,
} from 'flow/actions/operator-actions';
import { fetchNextScheduledProfiles } from 'flow/actions/extension-actions';
import { useHistory } from 'react-router-dom';
import { routes } from 'flow/types/routes';
import { getIsSinglePaneMode } from 'flow/selectors/selectors';
import { isOperatorKey } from 'flow/utils';
import { addSupervisorLogEntry } from 'flow/utils/browser-utils';
import {
    AgentMinimum,
    QueueMinimumWithFavoriteInfo,
} from 'flow/types/Supervisor';

import { TypedMap } from 'flow/types/TypedMap';
import SupervisorAgentsHeader from 'flow/components/pbx/supervisor/SupervisorAgentsHeader';
import SupervisorQueueRow from 'flow/components/pbx/supervisor/SupervisorQueueRow';
import SupervisorAgentRow from 'flow/components/pbx/supervisor/SupervisorAgentRow';
import throttle from 'lodash/throttle';
import {
    selectAgentsSortingDataInSelectedOperatorGroup,
    selectSelectedOperator,
    selectSelectedOperatorQueuesSortingData,
} from 'flow/selectors/SupervisorSelectors';
import {
    loadSelectedOperatorFromLocalStorage,
    loadSelectedQueueFromLocalStorage,
} from 'flow/actions/SupervisorActions';
import useAppContext from 'flow/hooks/useAppContext';
import BackButton from 'flow/components/ui-components/BackButton';
import SupervisorList from 'flow/components/pbx/supervisor/SupervisorList';

const FETCH_NEXT_SCHEDULED_PROFILE_THROTTLE_TIME = 5 * 1000;

const PbxOverview = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { i18next } = useAppContext();

    useEffect(() => {
        dispatch(loadSelectedQueueFromLocalStorage());
        dispatch(loadSelectedOperatorFromLocalStorage());
    }, [dispatch]);

    const selectedQueuesSortingData = useAppSelector(
        selectSelectedOperatorQueuesSortingData,
    );
    const selectedAgentsSortingData = useAppSelector(
        selectAgentsSortingDataInSelectedOperatorGroup,
    );

    const selectedOperator = useAppSelector(selectSelectedOperator);

    const isSinglePaneMode = useAppSelector(getIsSinglePaneMode);

    const listItems: React.JSX.Element[] = [];

    const addListItem = (item: React.JSX.Element) => {
        listItems.push(item);
    };

    const fetchNextScheduledProfilesThrottled = useMemo(() => {
        return throttle(
            () => dispatch(fetchNextScheduledProfiles(selectedOperator)),
            FETCH_NEXT_SCHEDULED_PROFILE_THROTTLE_TIME,
            { trailing: false }, //Makes it so that calls of the method within time above are not executed
        );
    }, [dispatch, selectedOperator]);

    useEffect(() => {
        addSupervisorLogEntry('Overview page displayed');
        return () => {
            addSupervisorLogEntry('Overview page no longer displayed');
        };
    }, []);

    useEffect(() => {
        dispatch(fetchOperators());
    }, [dispatch]);

    useEffect(() => {
        if (isOperatorKey(selectedOperator)) {
            dispatch(fetchOperatorData(selectedOperator));
        }
    }, [dispatch, selectedOperator]);

    useEffect(() => {
        fetchNextScheduledProfilesThrottled();
    }, [fetchNextScheduledProfilesThrottled]);

    const createQueueItem = (queue: TypedMap<QueueMinimumWithFavoriteInfo>) => {
        const queueItem = <SupervisorQueueRow queueKey={queue.get('key')} />;
        addListItem(queueItem);
    };

    const createAgentItem = (agent: TypedMap<AgentMinimum>) => {
        const extensionKey = agent.get('extensionKey');
        const agentItem = (
            <SupervisorAgentRow
                key={extensionKey}
                agentKey={extensionKey}
                onProfileOrEndTimeChanged={fetchNextScheduledProfilesThrottled}
            />
        );

        addListItem(agentItem);
    };

    const handleBack = () => {
        history.replace(routes['pbx']);
    };

    if (!selectedQueuesSortingData || !selectedAgentsSortingData) {
        return <Loading />;
    }

    const createQueuesOverviewHeader = () => {
        const component = <SupervisorOverviewComponent />;
        addListItem(component);
    };

    const createAgentsHeader = () => {
        const agentsHeader = <SupervisorAgentsHeader />;
        addListItem(agentsHeader);
    };

    const createSpacer = (height: number) => {
        const spacer = <Spacer height={height} />;
        addListItem(spacer);
    };

    // Create all items and put them in a list and put the items heights
    // in another list for VariableSizeWindowingList to render

    createSpacer(24);
    createQueuesOverviewHeader();
    selectedQueuesSortingData?.map(createQueueItem);
    createSpacer(42);
    createAgentsHeader();
    createSpacer(12);
    selectedAgentsSortingData?.map(createAgentItem);
    createSpacer(82);

    return (
        <ContentWrapper backgroundStyle="filled">
            <div
                id="main-wrapper"
                className="!bg-gray-150 grow flex-col flex-nowrap overflow-hidden border-white dark:!bg-white"
            >
                <header className="relative flex h-16 w-full items-center justify-between border-b px-4 font-bold">
                    {isSinglePaneMode ? (
                        <BackButton onClick={handleBack} />
                    ) : null}
                    <span className="absolute start-1/2 -translate-x-1/2 transform text-base">
                        {i18next.t('messages.overview')}
                    </span>
                </header>
                <SupervisorList items={listItems} />
            </div>
        </ContentWrapper>
    );
};

export default React.memo(PbxOverview);
